import React from 'react';
import { Link } from 'react-router-dom';
import { Ban } from 'lucide-react';
import { LegalPageLayout, Section, Callout, Bullets } from '@/components/LegalPageLayout';

const UPDATED = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const AcceptableUse = () => (
  <LegalPageLayout
    title="Acceptable Use Policy"
    lastUpdated={UPDATED}
    icon={Ban}
    testid="acceptable-use-page"
    intro={
      <p>
        This Acceptable Use Policy describes activities that are prohibited when using Option Wizard and its
        integrations. It is part of, and incorporated into, our{' '}
        <Link to="/terms" className="text-amber-300 hover:underline underline-offset-4">Terms of Service</Link>.
      </p>
    }
  >
    <Callout tone="amber" title="Your mandatory legal rights are unaffected">
      Nothing in this policy is intended to prohibit activity that you have a non-waivable legal right to perform under
      the laws that apply to you (for example, certain interoperability or lawful research rights).
    </Callout>

    <Section title="1. Security & Access">
      <Bullets items={[
        'Do not access accounts, data or areas of the service you are not authorized to access.',
        'Do not circumvent, disable or interfere with authentication, subscription or entitlement controls.',
        'Do not attempt to exploit vulnerabilities, or test/scan the platform\u2019s security without prior authorization.',
        'Do not steal, phish for, or misuse credentials or session tokens.',
      ]} />
    </Section>

    <Section title="2. Service Integrity">
      <Bullets items={[
        'Do not interfere with or disrupt the availability or performance of the service.',
        'Do not use bots, scrapers or automation to generate excessive or abusive request volumes.',
        'Do not introduce malware or any harmful or disruptive code.',
      ]} />
    </Section>

    <Section title="3. Data & Intellectual Property">
      <Bullets items={[
        'Do not perform unauthorized extraction, harvesting or bulk downloading of data from the platform.',
        'Do not redistribute, resell or publicly republish market data in violation of the data providers\u2019 terms or applicable licenses.',
        'Do not reverse engineer, decompile or disassemble the platform except to the extent such restriction is prohibited by law.',
      ]} />
    </Section>

    <Section title="4. Third-Party Integrations">
      <Bullets items={[
        'Only connect brokerage or third-party accounts that you own or are authorized to use.',
        'Do not misuse third-party integrations or violate the terms of those third-party services.',
      ]} />
    </Section>

    <Section title="5. AI & Safety Safeguards">
      <Bullets items={[
        'Do not attempt to manipulate, bypass or disable AI or security safeguards.',
        'Do not use the platform to generate unlawful, fraudulent or deceptive content.',
      ]} />
    </Section>

    <Section title="6. Lawful Use">
      <Bullets items={[
        'Do not use the platform for any illegal purpose or in violation of applicable laws or regulations.',
        'Do not use the platform to facilitate market manipulation or other prohibited financial conduct.',
      ]} />
    </Section>

    <Section title="7. Enforcement">
      <p>
        Violations may result in warnings, rate-limiting, suspension or termination of access, and where appropriate,
        referral to the relevant authorities. Report suspected misuse through our{' '}
        <Link to="/contact" className="text-amber-300 hover:underline underline-offset-4">contact form</Link>.
      </p>
    </Section>
  </LegalPageLayout>
);

export default AcceptableUse;
